import { Alert as MuiAlert, Box, styled } from '@mui/material';
import { ReactNode } from 'react';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ErrorIcon } from '@/icons/ErrorIcon';

export type AlertSeverity = 'success' | 'warning' | 'error' | 'info';

export interface AlertProps {
    severity: AlertSeverity;
    title?: string;
    message: string | ReactNode;
    action?: ReactNode;
    onClose?: () => void;
}

const StyledAlert = styled(MuiAlert)(({ theme, severity }) => {
    const colorMap = {
        success: {
            background: theme.palette.successBackground || '#ECFDF3',
            border: '#17B76A',
            icon: '#17B76A'
        },
        warning: {
            background: '#FEF0C7',
            border: '#F79009',
            icon: '#F79009'
        },
        error: {
            background: '#FEF3F2',
            border: '#F04438',
            icon: '#F04438'
        },
        info: {
            background: '#EFF8FF',
            border: '#1570EF',
            icon: '#1570EF'
        }
    };

    const colors = colorMap[severity as AlertSeverity] || colorMap.info;

    return {
        backgroundColor: colors.background,
        borderRadius: '8px',
        padding: '12px 16px',
        maxHeight: '40px',
        alignItems: 'center',
        boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.4)',
        '& .MuiAlert-icon': {
            color: colors.icon,
            padding: 0,
            marginRight: theme.spacing(1.5),
            fontSize: '20px',
            alignSelf: 'center'
        },
        '& .MuiAlert-message': {
            padding: 0,
            width: '100%',
            color: '#344054',
            display: 'flex',
            alignItems: 'center'
        },
        '& .MuiAlert-action': {
            padding: 0,
            marginRight: 0,
            marginLeft: theme.spacing(2),
            alignItems: 'center',
            alignSelf: 'center',
            paddingTop: 0
        }
    };
});

const iconMapping = {
    success: <CheckCircleIcon fontSize="inherit" />,
    warning: <WarningIcon fontSize="inherit" />,
    error: <ErrorIcon fontSize="inherit" />,
    info: <InfoIcon fontSize="inherit" />
};

export const Alert = ({
    severity,
    title,
    message,
    action,
    onClose
}: AlertProps) => {
    return (
        <StyledAlert
            sx={{ marginBottom: '24px' }}
            severity={severity}
            iconMapping={iconMapping}
            action={action}
            onClose={onClose}
        >
            <Box
                sx={{
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#667085',
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                {message}
            </Box>
        </StyledAlert>
    );
};
